import { useMemo, useState } from "react";
import clsx from "clsx";
import { db, inList } from "../../lib/api";
import { invalidate, useQuery } from "../../lib/hooks";
import { setQuery, useRoute } from "../../lib/router";
import { AREA_LABEL, LEAD_AREAS, useRefData, type Area } from "../../lib/refdata";
import { useSession } from "../../lib/session";
import { PageHead } from "../../components/Shell";
import { Empty, ErrorBox, Loading, Panel, Select, Sheet, useToast } from "../../components/ui";
import { Pager, usePaged } from "../../components/Pager";
import { openPerson } from "../../components/PersonDrawer";

type Cand = { person_id: string; full_name: string; phone: string | null; email: string | null; relationship: string | null; status: string | null;
  reasons: string[]; duplicate_of: string[] | null; touches: number };
const REASONS = ["Possible duplicate", "Name is a phone number", "No letters in the name", "Looks like a group chat", "Also on your team", "Not classified"];

/** Clean up a list: fix junk entries, classify contacts, archive, and merge duplicates. Better lists, better random draws. */
export default function Cleanup() {
  const ref = useRefData();
  const toast = useToast();
  const { can } = useSession();
  const { query } = useRoute();
  const area = (query.get("area") ?? "warm") as Area;
  const reason = query.get("reason") ?? "";
  const q = useQuery(`cleanup:${area}`, () => db.rpc<Cand[]>("cleanup_candidates", { p_area: area }));
  const rows = useMemo(() => (q.data ?? []).filter((r) => !reason || r.reasons.includes(reason)), [q.data, reason]);
  const paged = usePaged(rows, "cleanup", 25);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [rel, setRel] = useState("");
  const [merge, setMerge] = useState<Cand | null>(null);
  const [err, setErr] = useState<Error | null>(null);
  const editable = can("leads", "edit");
  const counts = Object.fromEntries(REASONS.map((r) => [r, (q.data ?? []).filter((x) => x.reasons.includes(r)).length]));
  const toggle = (id: string) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allOnPage = paged.view.every((r) => sel.has(r.person_id));

  async function bulk(patch: Record<string, string>, label: string) {
    setErr(null);
    try {
      const ids = [...sel];
      if (!ids.length) return;
      for (let i = 0; i < ids.length; i += 100)
        await db.update("person_areas", { area: `eq.${area}`, person_id: inList(ids.slice(i, i + 100)) }, patch);
      setSel(new Set()); invalidate("cleanup"); invalidate("people"); toast(`${label}: ${ids.length} people`);
    } catch (e) { setErr(e as Error); }
  }

  return (
    <>
      <PageHead title="Clean up lists" sub="Entries that need a decision: phone numbers or emojis as names, group chats, duplicates, people who are really on your team, and contacts not yet classified. Fix them here and your Today's 10 gets better." />
      <div className="filters">
        <Select value={area} onChange={(v) => setQuery({ area: v || "warm", reason: null })} options={LEAD_AREAS.map((a) => ({ value: a, label: AREA_LABEL[a] }))} style={{ width: 200 }} />
        <div className="chips">
          <button className={clsx("chip-check", !reason && "on")} onClick={() => setQuery({ reason: null })}>All {q.data?.length ?? ""}</button>
          {REASONS.filter((r) => counts[r]).map((r) => (
            <button key={r} className={clsx("chip-check", reason === r && "on")} onClick={() => setQuery({ reason: r })}>{r} · {counts[r]}</button>
          ))}
        </div>
      </div>
      <ErrorBox error={err || q.error} />
      {editable && sel.size > 0 && (
        <div className="bulkbar">
          <b>{sel.size} selected</b>
          <Select value={rel} onChange={setRel} options={ref.data?.list("WarmRelationship") ?? []} placeholder="Classify as…" style={{ width: 200 }} />
          <button className="btn small" disabled={!rel} onClick={() => bulk({ relationship: rel }, `Classified as ${rel}`)}>Apply</button>
          <button className="btn small" onClick={() => bulk({ status: "Archived" }, "Archived")}>Archive</button>
          <button className="btn small ghost" onClick={() => bulk({ status: "Do not contact" }, "Marked do not contact")}>Do not contact</button>
          <button className="btn small ghost" onClick={() => setSel(new Set())}>Clear</button>
        </div>
      )}
      <Panel>
        {q.loading && !q.data ? <Loading rows={8} /> : rows.length === 0 ? <Empty title="Nothing to clean up here">This list is in good shape.</Empty> : (<>
          <div className="table-wrap">
            <table className="data">
              <thead><tr>
                {editable && <th style={{ width: 32 }}><input type="checkbox" aria-label="Select page" checked={allOnPage}
                  onChange={() => setSel((s) => { const n = new Set(s); paged.view.forEach((r) => (allOnPage ? n.delete(r.person_id) : n.add(r.person_id))); return n; })} /></th>}
                <th>Name</th><th>Phone / email</th><th>Classified as</th><th>Why it's here</th><th className="num">Log entries</th><th />
              </tr></thead>
              <tbody>
                {paged.view.map((r) => (
                  <tr key={r.person_id}>
                    {editable && <td><input type="checkbox" aria-label={`Select ${r.full_name}`} checked={sel.has(r.person_id)} onChange={() => toggle(r.person_id)} /></td>}
                    <td><button className="linkish" onClick={() => openPerson(r.person_id)}><strong>{r.full_name || "(no name)"}</strong></button></td>
                    <td className="small">{r.phone ?? ""}{r.email && <div className="muted">{r.email}</div>}</td>
                    <td className="small">{r.relationship ?? <span className="muted">—</span>}</td>
                    <td><div className="chips">{r.reasons.map((x) => <span key={x} className={clsx("chip", x === "Possible duplicate" && "chip-warn")}>{x}</span>)}</div></td>
                    <td className="num">{r.touches}</td>
                    <td>{can("people_admin", "edit") && r.duplicate_of?.length ? <button className="btn small" onClick={() => setMerge(r)}>Merge…</button> : null}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager p={paged} noun="people" />
        </>)}
      </Panel>
      {merge && <MergeSheet c={merge} onClose={() => setMerge(null)} onDone={() => { setMerge(null); invalidate("cleanup"); invalidate("people"); }} />}
    </>
  );
}

export function MergeSheet({ c, onClose, onDone }: { c: Pick<Cand, "person_id" | "duplicate_of">; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const ids = [c.person_id, ...(c.duplicate_of ?? [])];
  const ppl = useQuery(`merge:${ids.join(",")}`, () => db.select<any>("people", {
    select: "id,full_name,phone,email,city,is_client,created_at,person_areas(area,relationship),team_members(agent_code)", id: inList(ids) }));
  const [keep, setKeep] = useState<string>(c.person_id);
  const [other, setOther] = useState<string>(c.duplicate_of?.[0] ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<Error | null>(null);
  const p = (id: string) => (ppl.data ?? []).find((x: any) => x.id === id);
  async function go() {
    setBusy(true); setErr(null);
    try {
      const r = await db.rpc<any>("merge_people", { p_keep: keep, p_drop: other });
      toast(`Merged: ${r.moved} records moved${r.dropped_as_duplicates ? `, ${r.dropped_as_duplicates} duplicates dropped` : ""}`);
      onDone();
    } catch (e) { setErr(e as Error); } finally { setBusy(false); }
  }
  const card = (id: string) => {
    const x = p(id);
    if (!x) return null;
    return (
      <label key={id} className={clsx("merge-card", keep === id && "keep", other === id && "drop")}>
        <div className="row-inline" style={{ margin: 0 }}>
          <input type="radio" name="keep" checked={keep === id} onChange={() => { setKeep(id); if (other === id) setOther(ids.find((y) => y !== id) ?? ""); }} /> Keep this one
          {ids.length > 2 && keep !== id && <><input type="radio" name="drop" checked={other === id} onChange={() => setOther(id)} /> Merge this one in</>}
        </div>
        <strong>{x.full_name}</strong>
        <div className="small muted">{[x.phone, x.email, x.city].filter(Boolean).join(" · ") || "No contact details"}</div>
        <div className="small">{(x.person_areas ?? []).map((a: any) => `${AREA_LABEL[a.area as Area] ?? a.area}${a.relationship ? ` (${a.relationship})` : ""}`).join(", ") || "Not on a list"}
          {x.is_client && " · Client"}{(() => { const tm = Array.isArray(x.team_members) ? x.team_members[0] : x.team_members; return tm ? ` · Team ${tm.agent_code ?? ""}` : ""; })()}</div>
      </label>
    );
  };
  return (
    <Sheet title="Merge duplicates" onClose={onClose} footer={<>
      <button className="btn" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={busy || !other || other === keep} onClick={go}>{busy ? "Merging…" : "Merge into the one I keep"}</button>
    </>}>
      <ErrorBox error={err} />
      <p className="small muted" style={{ marginTop: 0 }}>Everything on the other record (history, follow-ups, deals, tasks, family links, client details) moves to the one you keep. Empty fields are filled in from the other record. The other record goes to the Removed bin (it can't be un-merged), and the change history keeps both.</p>
      {ppl.loading && !ppl.data ? <Loading rows={3} /> : <div className="merge-grid">{ids.map(card)}</div>}
    </Sheet>
  );
}
